import React, { useMemo } from 'react';
import { ArrowRight, Check, Clock, Dumbbell, Flame, Layers, SkipForward, Sparkles, TrendingUp, Trophy, Weight } from 'lucide-react';
import { bestSet, describeSet, formatDuration, plural, progressHighlights, trackingKind, volumeKg, volumeVsLast } from './playerLogic';

// O fim do treino: o que fez, em números e exercício a exercício. O XP aparece só se acabou de o ganhar.

const Tile = ({ icon: Icon, value, label, testId }) => (
  <div className="wp-tile" data-testid={testId}>
    <Icon size={18} aria-hidden="true" />
    <span className="wp-tile-value">{value}</span>
    <span className="wp-tile-label">{label}</span>
  </div>
);

export default function FinishScreen({ summary, onDone, onProgress }) {
  const exercises = useMemo(() => summary.exercises || [], [summary.exercises]);
  const stats = useMemo(() => ({
    sets: exercises.reduce((n, e) => n + (e.sets || []).length, 0),
    volume: volumeKg(exercises),
    done: exercises.filter((e) => e.status === 'Completed').length,
    skipped: exercises.filter((e) => e.status === 'Skipped').length,
    substituted: exercises.filter((e) => e.wasSubstituted).length,
  }), [exercises]);

  const highlights = useMemo(() => progressHighlights(exercises), [exercises]);
  const volumeDelta = useMemo(() => volumeVsLast(exercises), [exercises]);
  // Estimativa do servidor (duração × kcal/min dos exercícios feitos). Sem estimativa, o indicador não aparece.
  const calories = summary.estimatedCaloriesKcal || 0;

  return (
    <div className="wp-screen wp-finish" data-testid="finish-screen">
      <div className="wp-finish-hero">
        <div className="wp-trophy" aria-hidden="true"><Trophy size={38} /></div>
        <h1 className="wp-h1">Treino concluído</h1>
        <p className="wp-muted">{summary.dayName} · {summary.planTitle}</p>
        {summary.xp?.awarded ? <p className="wp-xp" role="status" data-testid="xp"><Sparkles size={16} aria-hidden="true" /> +{summary.xp.xp} XP</p> : null}
      </div>

      <div className="wp-tiles">
        <Tile icon={Clock} value={formatDuration(summary.durationSeconds || 0)} label="Duração" testId="stat-duration" />
        <Tile icon={Layers} value={stats.sets} label="Séries" testId="stat-sets" />
        <Tile icon={Weight} value={`${stats.volume.toLocaleString('pt-PT')} kg`} label="Volume" testId="stat-volume" />
        <Tile icon={Dumbbell} value={`${stats.done}/${exercises.length}`} label={stats.skipped ? `Exercícios · ${stats.skipped} saltado${stats.skipped > 1 ? 's' : ''}` : 'Exercícios'} testId="stat-exercises" />
        {calories ? <Tile icon={Flame} value={`${calories.toLocaleString('pt-PT')} kcal`} label="Calorias" testId="stat-calories" /> : null}
      </div>

      {calories ? (
        <p className="wp-note wp-center" data-testid="calories-note">
          As calorias são uma estimativa: a duração do treino multiplicada pelas calorias por minuto dos exercícios que fez.
          Não medimos esforço nem batimento — serve de referência, não de medição.
        </p>
      ) : null}

      {highlights.length > 0 || volumeDelta ? (
        <section className="wp-finish-list wp-evolution" aria-label="Evolução desde a última vez" data-testid="evolution">
          <h2 className="wp-h3"><TrendingUp size={16} aria-hidden="true" /> Desde a última vez</h2>
          {volumeDelta ? (
            <p className="wp-evolution-volume" data-testid="evolution-volume">
              Volume {volumeDelta.percent > 0 ? <strong className="wp-up">+{volumeDelta.percent}%</strong> : volumeDelta.percent < 0 ? <strong>{volumeDelta.percent}%</strong> : <strong>igual</strong>}
              {' '}nos {plural(volumeDelta.exercises, 'exercício', 'exercícios')} que já tinha feito ({volumeDelta.before.toLocaleString('pt-PT')} → {volumeDelta.now.toLocaleString('pt-PT')} kg).
            </p>
          ) : null}
          {highlights.length > 0 ? (
            <ul className="wp-highlights">
              {highlights.map((h) => (
                <li key={h.name} className="wp-highlight"><Trophy size={15} aria-hidden="true" /><span className="wp-ex-name">{h.name}</span><span className="wp-up">{h.text}</span></li>
              ))}
            </ul>
          ) : null}
        </section>
      ) : null}

      <section className="wp-finish-list" aria-label="Resumo por exercício">
        <h2 className="wp-h3">Resumo</h2>
        <ol className="wp-summary">
          {exercises.map((e) => {
            const best = bestSet(e);
            const kind = trackingKind(e.trackingType);
            return (
              <li key={e.id} className={`wp-summary-row status-${e.status}`}>
                <span className="wp-ex-badge" aria-hidden="true">{e.status === 'Skipped' ? <SkipForward size={14} /> : <Check size={16} strokeWidth={3} />}</span>
                <span className="wp-ex-text">
                  <span className="wp-ex-name">{e.name}{e.wasSubstituted ? <span className="wp-ex-sub"> · substituído</span> : null}</span>
                  <span className="wp-ex-meta">
                    {e.status === 'Skipped' ? 'Saltado' : `${plural((e.sets || []).length, 'série', 'séries')}${best ? ` · melhor: ${describeSet(best, e.trackingType)}${kind.usesTime ? '' : ' reps'}` : ''}`}
                  </span>
                </span>
              </li>
            );
          })}
        </ol>
      </section>

      {summary.nextSession ? <NextSession next={summary.nextSession} /> : null}

      <div className="wp-actions wp-finish-actions">
        {onProgress ? (
          <>
            <button type="button" className="wp-btn wp-btn-primary wp-btn-xl" onClick={onProgress} data-testid="finish-progress">Ver o meu progresso</button>
            <button type="button" className="wp-btn wp-btn-ghost wp-btn-xl" onClick={onDone} data-testid="finish-done">Voltar ao treino</button>
          </>
        ) : (
          <button type="button" className="wp-btn wp-btn-primary wp-btn-xl" onClick={onDone} data-testid="finish-done">Voltar ao treino</button>
        )}
      </div>
    </div>
  );
}

/**
 * O que vem a seguir no plano, com a sugestão de cada exercício. O servidor calcula-a a partir do que o atleta REALMENTE
 * registou (e diz-o quando não há histórico); o plano não muda. Os valores do plano e as sugestões aparecem separados.
 */
function NextSession({ next }) {
  const exercises = next.exercises || [];
  return (
    <section className="wp-finish-list wp-next" aria-label="Próxima sessão" data-testid="next-session">
      <h2 className="wp-h3"><ArrowRight size={16} aria-hidden="true" /> Próxima sessão</h2>
      <p className="wp-next-title" data-testid="next-session-title">
        <strong>{next.dayName}</strong> · {plural(next.exerciseCount ?? exercises.length, 'exercício', 'exercícios')}
        {next.sameDay ? ' · o plano só tem este dia: repete-se' : next.afterDayName ? ` · a seguir a ${next.afterDayName}` : ''}
      </p>
      <ul className="wp-next-list">
        {exercises.map((e, i) => (
          <li key={`${e.name}-${i}`} className="wp-next-row" data-testid="next-session-exercise">
            <span className="wp-ex-name">{e.name}</span>
            {e.prescription ? <span className="wp-ex-meta" data-testid="next-prescription">Plano: {e.prescription}</span> : null}
            {e.suggestion?.text ? <span className="wp-ex-meta wp-next-suggestion" data-testid="next-suggestion">{e.suggestion.text}</span> : null}
          </li>
        ))}
      </ul>
      <p className="wp-note">As sugestões partem do que registou; o plano não muda.</p>
    </section>
  );
}
